/**
 * AdminCustomerHealth Page
 *
 * Per-customer health dashboard — Stripe billing × platform activity
 * (jobs -> sourced -> sent -> replies). GitHub issue "[Admin]
 * Per-customer health dashboard — Stripe billing × platform activity
 * (jobs->sourced->sent->replies)".
 *
 * On 2026-06-22 we could not answer "is the platform working for paying
 * customers" from the product — the DB showed every plan as $0 (#197),
 * so paying customers were invisible in-app. A manual Stripe pull found
 * 20 active paying subs (~$19K MRR / ~$228K ARR); a manual join to DB
 * activity showed ~19 actively sending with 45 replies. This page is
 * that manual join, made a standing admin feature: fetches
 * `GET /api/admin/customer-health`
 * (`apps/workers/src/api/admin/customerHealth.ts`) and renders every
 * paying customer's billing + activity in one sortable table, with the
 * health flag highlighting anyone paying but not getting value.
 *
 * AC: "Given an admin is authenticated and 20 paying customers exist
 * across Stripe and DB, when the admin opens the Customer Health
 * dashboard at /admin, then a table renders 20 rows, each showing email,
 * plan, amount, interval, jobs count, candidates sourced, outreach sent
 * (30d), replies, and a colored health flag."
 * AC: "Given a paying customer with an active Stripe sub but 0 jobs and
 * 0 sends, when the dashboard loads, then that customer's row shows the
 * red '🔴 paying but no jobs/sends' health flag."
 *
 * RBAC: mounted behind `AdminGuard` (see `router.tsx`) — only
 * `is_superuser` users can reach this route; the API endpoint itself
 * independently re-verifies superuser status server-side (never trusts
 * the client-side guard alone) and returns zero billing data to a
 * non-admin caller (403).
 *
 * Sort/filter is done client-side (no server pagination) — this issue's
 * own scope note: "First deliverable: a one-time generated table
 * (markdown) to share with Robert now; then wire it into the admin
 * panel" — ~20 rows at this issue's scale doesn't warrant the
 * server-side pagination machinery `Subscriptions.tsx` uses for a
 * larger, ever-growing customer list.
 *
 * Related: #197, #196, #123, #122, #222,
 * `scripts/generateCustomerHealthReport.ts` (the markdown snapshot for
 * Robert, sharing this exact same backend join logic).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminCustomerHealth, reportApiError } from '@/lib/api/apiClient';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  AdminCustomerHealthRow,
  AdminCustomerHealthFlag,
  CustomerHealthSortField,
} from '@/lib/validation/customerHealth';

const HEALTH_FLAG_BADGE: Record<AdminCustomerHealthFlag, { emoji: string; label: string; className: string }> = {
  green: { emoji: '🟢', label: 'Active + replies', className: 'bg-green-100 text-green-800' },
  yellow: { emoji: '🟡', label: 'Active, no replies', className: 'bg-yellow-100 text-yellow-800' },
  red: { emoji: '🔴', label: 'Paying, no jobs/sends', className: 'bg-red-100 text-red-800' },
};

function formatAmountCents(amountCents: number, currency: string): string {
  return `${(amountCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency.toUpperCase() || 'USD',
    minimumFractionDigits: 2,
  })}`;
}

function compareRows(
  a: AdminCustomerHealthRow,
  b: AdminCustomerHealthRow,
  field: CustomerHealthSortField
): number {
  switch (field) {
    case 'email':
      return a.email.localeCompare(b.email);
    case 'planName':
      return a.planName.localeCompare(b.planName);
    case 'amountCents':
      return a.amountCents - b.amountCents;
    case 'jobsOpen':
      return a.jobsOpen + a.jobsArchived - (b.jobsOpen + b.jobsArchived);
    case 'candidatesSourced':
      return a.candidatesSourced - b.candidatesSourced;
    case 'outreachSent30d':
      return a.outreachSent30d - b.outreachSent30d;
    case 'replies':
      return a.replies - b.replies;
    case 'healthFlag':
      return a.healthFlag.localeCompare(b.healthFlag);
    default:
      return 0;
  }
}

export default function AdminCustomerHealth() {
  const navigate = useNavigate();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [sortBy, setSortBy] = useState<CustomerHealthSortField>('healthFlag');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading, isError, error, refetch, isFetching } =
    useAdminCustomerHealth(forceRefresh);

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load customer health dashboard');
    }
  }, [isError, error]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.data].sort((a, b) => compareRows(a, b, sortBy));
    return sortOrder === 'desc' ? rows.reverse() : rows;
  }, [data, sortBy, sortOrder]);

  const toggleSort = (field: CustomerHealthSortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortIndicator = (field: CustomerHealthSortField) =>
    sortBy === field ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';

  const handleRefreshNow = () => {
    setForceRefresh(true);
    // Reset back to false after the forced query settles so a
    // subsequent normal reload goes back through the cache, matching
    // the endpoint's `?refresh=1` semantics (a one-shot bypass, not a
    // permanent "never cache" toggle).
    void refetch().finally(() => setForceRefresh(false));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">
            Error Loading Customer Health
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch customer health'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const redCount = data.data.filter((r) => r.healthFlag === 'red').length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold">Customer Health</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Stripe billing × platform activity, per paying customer.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRefreshNow}
                disabled={isFetching}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
              >
                {isFetching ? 'Refreshing…' : 'Refresh now'}
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span data-testid="customer-health-summary">
              {data.total_count} paying customer{data.total_count === 1 ? '' : 's'}
              {redCount > 0 && (
                <span className="ml-2 text-red-700 font-semibold">
                  · {redCount} not getting value 🔴
                </span>
              )}
            </span>
            <span>
              {data.cached ? 'Served from cache' : 'Freshly computed'} at{' '}
              {new Date(data.generated_at).toLocaleString()}
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {sortedRows.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No active paying customers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('email')}
                  >
                    Email{sortIndicator('email')}
                  </th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('planName')}
                  >
                    Plan{sortIndicator('planName')}
                  </th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('amountCents')}
                  >
                    Amount{sortIndicator('amountCents')}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">Linked?</th>
                  <th className="px-4 py-2 text-left font-semibold">Stripe Sub ID</th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('jobsOpen')}
                  >
                    Jobs (open/archived){sortIndicator('jobsOpen')}
                  </th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('candidatesSourced')}
                  >
                    Sourced{sortIndicator('candidatesSourced')}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">Reachable %</th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('outreachSent30d')}
                  >
                    Sent (30d){sortIndicator('outreachSent30d')}
                  </th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('replies')}
                  >
                    Replies{sortIndicator('replies')}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">Last Activity</th>
                  <th
                    className="px-4 py-2 text-left font-semibold cursor-pointer select-none"
                    onClick={() => toggleSort('healthFlag')}
                  >
                    Health{sortIndicator('healthFlag')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const flag = HEALTH_FLAG_BADGE[row.healthFlag];
                  return (
                    <tr
                      key={row.stripeSubscriptionId}
                      data-testid="customer-health-row"
                      data-health-flag={row.healthFlag}
                      className="border-b border-border hover:bg-muted/50"
                    >
                      <td className="px-4 py-2">{row.email}</td>
                      <td className="px-4 py-2">
                        {row.planName} <span className="text-muted-foreground">/{row.interval}</span>
                      </td>
                      <td className="px-4 py-2">{formatAmountCents(row.amountCents, row.currency)}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            row.linkedDbUser ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {row.linkedDbUser ? 'Y' : 'N'}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {row.stripeSubscriptionIdPresent ? row.stripeSubscriptionId : '—'}
                      </td>
                      <td className="px-4 py-2">
                        {row.jobsOpen} / {row.jobsArchived}
                      </td>
                      <td className="px-4 py-2">{row.candidatesSourced}</td>
                      <td className="px-4 py-2">{row.reachablePercent}%</td>
                      <td className="px-4 py-2">{row.outreachSent30d}</td>
                      <td className="px-4 py-2">{row.replies}</td>
                      <td className="px-4 py-2 text-xs">
                        {row.lastActivity ? new Date(row.lastActivity).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${flag.className}`}
                        >
                          {flag.emoji} {flag.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
